import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { useEdges, useNodes } from '@xyflow/react'
import { useEffect, useMemo } from 'react'
import { useLivekitEnabled } from 'v2/shared/hooks/use-livekit-enabled'
import { z } from 'zod'
import { NodevarList } from '../components/nodevar-list'
import { v2validations } from '../config/validation.config'
import type { EndConversationNodeData, SidebarComponentProps } from '../types'
import { isConnectedToAiAgentV2Call } from '../utils/ai-agent-utils'
import { GenericSidebarComponent } from './sidebar.base'

type FormValues = EndConversationNodeData['config']

const fields = (nodeVars: string[]): GenericFieldConfig<FormValues> => {
  const validations = v2validations({ nodeVars }).end_conversation
  return [
    {
      name: 'name',
      label: 'Name',
      zodValidation: validations.shape.name,
      componentType: 'input',
    },
    {
      name: 'end_message' as const,
      label: 'Closing message instructions',
      componentType: 'textarea' as const,
      zodValidation: z.string().optional().nullable(),
      inputProps: {
        placeholder: 'Instructions for what the AI should say before ending the call',
        minHeight: 80,
        endIcon: (
          <NodevarList nodevars={nodeVars} inputElementSelector='textarea[name="end_message"]' />
        ),
      },
    },
  ]
}

// NODE_GEN: Update this code, do not commit directly
// Remove this comment after updating the code

export const EndConversationSidebarComponent = (
  props: SidebarComponentProps<EndConversationNodeData>,
) => {
  const { nodeVars, selectedNode, unselectNode } = props
  const { isLivekitEnabled } = useLivekitEnabled()
  const nodes = useNodes()
  const edges = useEdges()

  const showSidebar = useMemo(() => {
    if (!isLivekitEnabled || !selectedNode?.id) return false
    return isConnectedToAiAgentV2Call(selectedNode.id, nodes, edges)
  }, [isLivekitEnabled, selectedNode?.id, nodes, edges])

  useEffect(() => {
    if (!showSidebar) {
      unselectNode(false)
    }
  }, [showSidebar, unselectNode])

  if (!showSidebar) return null

  const formFields = fields(nodeVars)

  return <GenericSidebarComponent fields={formFields} {...props} />
}
